import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { CustomField, CUSTOM_FIELD_TYPES, Profile, UserGroup } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  selector: 'app-automation-custom-field-input',
  templateUrl: './automation-custom-field-input.component.html',
  styleUrls: ['./automation-custom-field-input.component.scss'],
  standalone: true, // Make it standalone
  imports: [CommonModule, FormsModule, IonicModule], // Import necessary modules
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationCustomFieldInputComponent implements OnInit, OnChanges {
  @Input() customField: CustomField | null = null;
  // Value is now expected to be the full object(s), not just ID(s)
  // e.g., Profile, UserGroup, {value, label} for external, primitive for internal select/text/date/checkbox
  @Input() value: any;
  // Lookups input removed - no longer needed here
  @Output() valueChange = new EventEmitter<any>();

  @Input() projectId: string | null = null;

  // Expose enum to template
  CUSTOM_FIELD_TYPES = CUSTOM_FIELD_TYPES;

  constructor(
    private searchablePopoverService: SearchablePopoverService,
    private apiService: ApiService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initializeComponentState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to input changes, especially if customField changes or value is set from parent
    if (changes['customField'] || changes['value']) {
      this.initializeComponentState();

      // If it's an external select and the value changed, try to fetch labels
      if (this.customField?.externalSelect && changes['value']) {
        this.fetchAndAssignExternalLabels();
      }
    }
  }

  private initializeComponentState() {
    // Ensure value is array for multi-selects if it's not already
    // Handle case where initial value might be null/undefined
    if (this.isMultiSelectType() && !Array.isArray(this.value)) {
      this.value = this.value ? [this.value] : [];
    }
    // Trigger change detection if needed after potential value transformation
    this.changeDetectorRef.markForCheck();
  }

  // Generic change handler for simple inputs (text, date, checkbox, internal select)
  onValueChange(newValue: any): void {
    // Special handling for ion-toggle (detail.checked)
    const finalValue = typeof newValue === 'object' && newValue?.detail ? newValue.detail.checked : newValue;
    if (finalValue !== this.value) {
      this.value = finalValue;
      this.valueChange.emit(this.value); // Emit the primitive value directly
      this.changeDetectorRef.markForCheck();
    }
  }

  // --- Popover Triggers --- Emit Full Objects ---

  async openUserSelectPopover(isMulti: boolean = false, event?: Event): Promise<void> {
    this.searchablePopoverService.users({
      event: event || window.event,
      // Pass current selection ID(s) to popover if needed
      // selectedUserId: !isMulti && this.value ? this.value.id : undefined,
      // selectedUserIds: isMulti && Array.isArray(this.value) ? this.value.map(u => u.id) : undefined,
      callback: (id: string, name: string, user: Profile) => {
        if (isMulti) {
          if (!Array.isArray(this.value)) {
            this.value = [];
          }
          // Add full user object if not already selected based on ID
          if (!this.value.some((u: Profile) => u.id === user.id)) {
            this.value = [...this.value, user];
          }
        } else {
          this.value = user; // Store the full user object
        }
        this.valueChange.emit(this.value); // Emit the full object or array of objects
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  async openUserGroupSelectPopover(isMulti: boolean = false, event?: Event): Promise<void> {
    this.searchablePopoverService.userGroups({
      event: event || window.event,
      // Pass current selection ID(s) to popover if needed
      // selectedGroupId: !isMulti && this.value ? this.value.id : undefined,
      // selectedGroupIds: isMulti && Array.isArray(this.value) ? this.value.map(g => g.id) : undefined,
      callback: (id: string, name: string, group: UserGroup) => {
        if (isMulti) {
          if (!Array.isArray(this.value)) {
            this.value = [];
          }
          // Add full group object if not already selected based on ID
          if (!this.value.some((g: UserGroup) => g.id === group.id)) {
            this.value = [...this.value, group];
          }
        } else {
          this.value = group; // Store the full group object
        }
        this.valueChange.emit(this.value); // Emit the full object or array of objects
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  async openExternalSelectPopover(isMulti: boolean = false, event?: Event): Promise<void> {
    if (!this.customField?.externalSelect) return;
    this.searchablePopoverService.customFieldOptions({
      event: event || window.event,
      field: this.customField,
      projectId: this.projectId,
      context: {},
      // Pass current selection value(s) to popover if needed
      // selectedValue: !isMulti && this.value ? this.value.value : undefined,
      // selectedValues: isMulti && Array.isArray(this.value) ? this.value.map(o => o.value) : undefined,
      callback: (id: string, name: string, object: { id: string; label: string }) => {
        // object is the { value, label } object from loadData
        if (isMulti) {
          if (!Array.isArray(this.value)) {
            this.value = [];
          }
          // Store { value, label } object, ensure no duplicates based on value
          if (!this.value.some((opt: { id: string }) => opt.id === object.id)) {
            this.value = [...this.value, object];
          }
        } else {
          this.value = object; // Store the { value, label } object
          // Close popover manually for single select if desired
          // this.popoverCtrl.dismiss();
        }
        this.valueChange.emit(this.value); // Emit the full object or array of objects
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  // --- Helpers --- Working with Full Objects ---

  isMultiSelectType(): boolean {
    return [
      CUSTOM_FIELD_TYPES.MULTI_SELECT,
      CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
      CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
    ].includes(this.customField?.type as any);
  }

  // Compare function used by ion-select internal primitive values
  // For object-based values, Angular typically compares by reference.
  // If finer control is needed for object comparison in selects (rarely needed here),
  // you might need a custom compare function based on object IDs.
  areValuesEqual(val1: any, val2: any): boolean {
    return val1 === val2;
  }

  // Resolve display label directly from the value object
  private resolveDisplayLabel(value: any): string {
    if (value === null || value === undefined) {
      // Check if this is the main value (not a chip) to display 'None'
      if (!Array.isArray(this.value) && this.value === value) {
        return 'None';
      }
      return ''; // Return empty for null/undefined chip values
    }

    // Handle simple primitives directly (Text, Date, Checkbox, Internal Select)
    if (typeof value !== 'object') {
      return String(value);
    }

    // Handle object types (User, Group, External Select)
    if (value.name) return value.name; // User, Group
    if (value.label) return value.label; // External Select {value, label}

    // Fallback (should ideally not be reached if value is a proper object)
    console.warn('Could not resolve label for value:', value, 'Field:', this.customField?.label);
    return String(value.id || value.value || ''); // Try common ID/value props
  }

  // Public getter for single value display
  getDisplayValue(value: any): string {
    return this.resolveDisplayLabel(value);
  }

  // Public getter for chip display
  getChipDisplayValue(item: any): string {
    return this.resolveDisplayLabel(item);
  }

  // --- Multi-select Chip Handlers ---
  removeValue(itemToRemove: any): void {
    if (!this.isMultiSelectType() || !Array.isArray(this.value)) return;

    // Get the unique identifier for comparison (id for User/Group, value for External)
    const idToRemove = itemToRemove?.id ?? itemToRemove?.value ?? itemToRemove;

    const newValue = this.value.filter((item) => {
      const currentId = item?.id ?? item?.value ?? item;
      return currentId !== idToRemove;
    });

    if (this.value.length !== newValue.length) {
      this.value = newValue;
      this.valueChange.emit(this.value); // Emit updated array of full objects
      this.changeDetectorRef.markForCheck();
    }
  }

  // --- TrackBy Functions ---

  // Track by unique identifier (id or value)
  trackByUniqueId(index: number, item: any): any {
    // For primitives in internal selects
    if (typeof item !== 'object' || item === null) {
      return item;
    }
    // For objects (User, Group, External Option)
    return item.id ?? item.value; // Use id for User/Group, value for external {value, label}
  }

  // Fetches all options for the external select and updates the labels
  // on the current value object(s) if they seem incomplete.
  private async fetchAndAssignExternalLabels(): Promise<void> {
    if (!this.customField?.externalSelect || !this.value) {
      return; // Nothing to do if not external or no value
    }

    // Basic check: Do we already have labels different from values?
    const needsFetching = (item: any) =>
      typeof item === 'object' && item !== null && (!item.label || item.label === item.value);

    let shouldFetch = false;
    if (Array.isArray(this.value)) {
      shouldFetch = this.value.some(needsFetching);
    } else {
      shouldFetch = needsFetching(this.value);
    }

    if (!shouldFetch) {
      return; // Labels seem okay, skip fetch
    }

    try {
      const functionName = this.customField.externalSelect;
      const allOptions: { label: string; value: string }[] = await this.apiService.postPromise(
        '/automations/get-external-select-options',
        {
          functionName: functionName,
          query: '', // Fetch all options to find the matching labels
          context: {},
        },
      );

      const optionsMap = new Map(allOptions.map((opt) => [String(opt.value), opt.label]));

      // Update labels on the existing value object(s)
      let updated = false;
      const updateLabel = (item: any) => {
        if (typeof item === 'object' && item !== null && item.value !== undefined) {
          const stringValue = String(item.value);
          const correctLabel = optionsMap.get(stringValue);
          if (correctLabel && item.label !== correctLabel) {
            item.label = correctLabel;
            updated = true;
          }
        }
        return item; // Return item potentially modified
      };

      if (Array.isArray(this.value)) {
        this.value = this.value.map(updateLabel);
      } else {
        this.value = updateLabel(this.value);
      }

      if (updated) {
        // console.log('Labels updated for', this.customField.label, this.value);
        this.changeDetectorRef.markForCheck(); // Trigger UI update with fetched labels
      }
    } catch (err) {
      console.error(`Error fetching external options for hydration ${this.customField.externalSelect}`, err);
      // Even on error, trigger check detection in case fallback labels should be shown
      this.changeDetectorRef.markForCheck();
    }
  }
}
